import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Mic, MapPin, CheckCircle2, AlertTriangle, Volume2, X,
  Plus, Keyboard, Trash2, Loader2, Info, ShieldCheck,
  Navigation, Circle,
} from 'lucide-react';

import { ETAPA } from '../../constants/vistoria';

/**
 * ============================================================
 * COMPONENTE: VistoriaEspacial_MapaVoz
 * ------------------------------------------------------------
 * Mapeamento de evidências sobre a planta do imóvel.
 *   ✔ Detecção de suporte a SpeechRecognition
 *   ✔ Fallback manual (input de texto) para Safari/iOS
 *   ✔ Lista de cômodos padrão editável
 *   ✔ Pins com ID estável + persistência opcional
 *   ✔ Tratamento de permissão de microfone
 * VERSÃO: 2.0.0
 * ============================================================
 */

/* =========================
   CÔMODOS PADRÃO (NBR + uso comum)
========================= */

const COMODOS_PADRAO = [
  'SALA DE ESTAR',
  'SALA DE JANTAR',
  'COZINHA',
  'SUÍTE MASTER',
  'QUARTO',
  'BANHEIRO SOCIAL',
  'BANHEIRO SUÍTE',
  'ÁREA DE SERVIÇO',
  'VARANDA',
  'GARAGEM',
  'FACHADA',
  'QUINTAL',
];

/* =========================
   DETECÇÃO DE SUPORTE
========================= */

function obterSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/* =========================
   COMPONENTE
========================= */

export default function VistoriaEspacial_MapaVoz({
  plantaImg = null,
  reportNumber = 'REPORT-2026-MH',
  fatorId = ETAPA.MAPA,
  pins: pinsControlados,
  onChangePins,
  onNovoPin,
  comodosIniciais,
}) {
  /* ---------- suporte a voz (detecção única) ---------- */
  const suporteVoz = useMemo(() => obterSpeechRecognition(), []);

  /* ---------- cômodos (padrão + customizados) ---------- */
  const [comodos, setComodos] = useState(
    () => [...(comodosIniciais || COMODOS_PADRAO)]
  );
  const [novoComodo, setNovoComodo] = useState('');
  const [mostrarGerenciadorComodos, setMostrarGerenciadorComodos] = useState(false);

  /* ---------- estado de captura ---------- */
  const [isOuvindo, setIsOuvindo] = useState(false);
  const [modo, setModo] = useState(suporteVoz ? 'VOZ' : 'MANUAL');
  const [localDetectado, setLocalDetectado] = useState('');
  const [inputManual, setInputManual] = useState('');
  const [permissao, setPermissao] = useState('UNKNOWN'); // UNKNOWN | OK | NEGADA | ERRO
  const [erro, setErro] = useState(null);

  /* ---------- pins ---------- */
  const [pinsInternos, setPinsInternos] = useState(pinsControlados || []);
  const pins = pinsControlados !== undefined ? pinsControlados : pinsInternos;

  /* ---------- refs ---------- */
  const recognitionRef = useRef(null);
  const containerRef = useRef(null);

  /* ---------- sincroniza pins com o pai se mudar externamente ---------- */
  useEffect(() => {
    if (pinsControlados !== undefined) {
      setPinsInternos(pinsControlados);
    }
  }, [pinsControlados]);

  /* ---------- atualiza estado dos pins ---------- */
  const setPins = useCallback((atualizador) => {
    setPinsInternos(prev => {
      const novo = typeof atualizador === 'function' ? atualizador(prev) : atualizador;
      if (typeof onChangePins === 'function') onChangePins(novo);
      return novo;
    });
  }, [onChangePins]);

  /* ---------- cleanup do recognition ---------- */
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* silencioso */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  /* =========================
     CAPTURA POR VOZ
  ========================= */

  const iniciarEscuta = useCallback(() => {
    setErro(null);

    if (!suporteVoz) {
      setModo('MANUAL');
      setErro('Reconhecimento de voz não suportado neste navegador. Use o modo manual.');
      return;
    }

    if (isOuvindo) return;

    try {
      const recognition = new suporteVoz();
      recognition.lang = 'pt-BR';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 3;

      recognition.onstart = () => {
        setIsOuvindo(true);
        setPermissao('OK');
      };

      recognition.onresult = (event) => {
        const alternativas = Array.from(event.results[0]).map(r => r.transcript.toUpperCase());

        // Tenta match exato primeiro, depois match parcial
        let encontrado = null;
        for (const transcricao of alternativas) {
          const exato = comodos.find(c => transcricao === c);
          if (exato) { encontrado = exato; break; }

          const parcial = comodos.find(c => transcricao.includes(c));
          if (parcial && !encontrado) encontrado = parcial;
        }

        if (encontrado) {
          setLocalDetectado(encontrado);
          setErro(null);
        } else {
          setLocalDetectado('');
          setErro(
            `Local "${alternativas[0]}" não reconhecido. ` +
            `Diga um dos cômodos cadastrados ou use o modo manual.`
          );
        }
      };

      recognition.onerror = (event) => {
        setIsOuvindo(false);
        const mapa = {
          'not-allowed': 'Permissão de microfone negada. Habilite nas configurações do navegador.',
          'service-not-allowed': 'Serviço de reconhecimento indisponível.',
          'no-speech': 'Nenhuma fala detectada. Tente novamente.',
          'audio-capture': 'Microfone não encontrado.',
          'network': 'Falha de rede no reconhecimento de voz.',
          'aborted': null, // cancelamento do usuário — silencioso
        };
        const msg = mapa[event.error];
        if (msg) setErro(msg);
        if (event.error === 'not-allowed') setPermissao('NEGADA');
      };

      recognition.onend = () => {
        setIsOuvindo(false);
        recognitionRef.current = null;
      };

      recognitionRef.current = recognition;
      recognition.start();

    } catch (err) {
      setIsOuvindo(false);
      setErro(`Falha ao iniciar reconhecimento: ${err.message}`);
    }
  }, [suporteVoz, isOuvindo, comodos]);

  const pararEscuta = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* silencioso */ }
      recognitionRef.current = null;
    }
    setIsOuvindo(false);
  }, []);

  /* =========================
     CAPTURA MANUAL
  ========================= */

  const aplicarComodoManual = useCallback((comodo) => {
    const alvo = String(comodo || inputManual).trim().toUpperCase();
    if (!alvo) {
      setErro('Informe o nome do cômodo antes de marcar.');
      return false;
    }

    // Match direto ou parcial
    const encontrado =
      comodos.find(c => c === alvo) ||
      comodos.find(c => alvo.includes(c)) ||
      null;

    if (!encontrado) {
      setErro(
        `"${alvo}" não está na lista de cômodos. ` +
        `Adicione-o no gerenciador de cômodos ou escolha um existente.`
      );
      return false;
    }

    setLocalDetectado(encontrado);
    setInputManual('');
    setErro(null);
    return true;
  }, [comodos, inputManual]);

  /* =========================
     GERENCIADOR DE CÔMODOS
  ========================= */

  const adicionarComodo = useCallback(() => {
    const novo = novoComodo.trim().toUpperCase();
    if (!novo) return;
    if (comodos.includes(novo)) {
      setErro(`"${novo}" já está na lista.`);
      return;
    }
    setComodos(prev => [...prev, novo]);
    setNovoComodo('');
    setErro(null);
  }, [novoComodo, comodos]);

  const removerComodo = useCallback((comodo) => {
    // Não permite remover se há pins vinculados
    const pinsVinculados = pins.filter(p => p.local === comodo).length;
    if (pinsVinculados > 0) {
      setErro(
        `"${comodo}" tem ${pinsVinculados} pin(s) vinculado(s). ` +
        `Remova os pins primeiro.`
      );
      return;
    }
    setComodos(prev => prev.filter(c => c !== comodo));
    if (localDetectado === comodo) setLocalDetectado('');
    setErro(null);
  }, [pins, localDetectado]);

  /* =========================
     MARCAÇÃO DE PIN
  ========================= */

  const confirmarPosicaoPin = useCallback((e) => {
    if (!plantaImg) {
      setErro('Faça o upload da planta antes de marcar pins.');
      return;
    }
    if (!localDetectado) {
      setErro('Diga ou digite o nome do compartimento antes de marcar no mapa.');
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = +(((e.clientX - rect.left) / rect.width) * 100).toFixed(2);
    const y = +(((e.clientY - rect.top) / rect.height) * 100).toFixed(2);

    // Guarda contra clique fora da imagem
    if (x < 0 || x > 100 || y < 0 || y > 100) return;

    const novoPin = {
      id: `PIN-${Date.now().toString(36).toUpperCase()}`,
      local: localDetectado,
      x,
      y,
      fatorId,
      reportNumber,
      timestamp: new Date().toISOString(),
    };

    setPins(prev => [...prev, novoPin]);
    if (typeof onNovoPin === 'function') onNovoPin(novoPin);

    setLocalDetectado('');
    setErro(null);
  }, [plantaImg, localDetectado, fatorId, reportNumber, setPins, onNovoPin]);

  const removerPin = useCallback((id) => {
    setPins(prev => prev.filter(p => p.id !== id));
  }, [setPins]);

  const limparTodosPins = useCallback(() => {
    if (pins.length === 0) return;
    if (!window.confirm(`Remover todos os ${pins.length} pin(s)?`)) return;
    setPins([]);
  }, [pins.length, setPins]);

  /* =========================
     ESTATÍSTICAS
========================= */

  const estatisticas = useMemo(() => {
    const porComodo = pins.reduce((acc, p) => {
      acc[p.local] = (acc[p.local] || 0) + 1;
      return acc;
    }, {});
    return { total: pins.length, porComodo };
  }, [pins]);

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="bg-slate-900 rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl text-slate-200">

      {/* =========================
          BARRA DE COMANDO
      ========================= */}
      <div className="bg-slate-950 p-6 lg:p-8 flex flex-wrap items-center gap-4 border-b border-white/5">

        {/* MIC / STATUS */}
        <div className="flex items-center gap-4 flex-1 min-w-[240px]">
          {suporteVoz && modo === 'VOZ' ? (
            <button
              type="button"
              onClick={isOuvindo ? pararEscuta : iniciarEscuta}
              disabled={permissao === 'NEGADA'}
              className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all shrink-0 ${
                isOuvindo
                  ? 'bg-red-600 text-white animate-pulse'
                  : permissao === 'NEGADA'
                    ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-500'
              }`}
              title={
                permissao === 'NEGADA'
                  ? 'Permissão de microfone negada'
                  : isOuvindo
                    ? 'Parar escuta'
                    : 'Iniciar escuta'
              }
            >
              {isOuvindo ? <X size={22} /> : <Mic size={22} />}
            </button>
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center shrink-0">
              <Keyboard size={22} className="text-slate-500" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <span className="text-[9px] text-slate-500 tracking-[0.3em] block">
              {suporteVoz ? 'DIRECIONAMENTO POR VOZ' : 'MODO MANUAL'}
            </span>
            <p className={`text-[14px] tracking-tight leading-tight mt-0.5 truncate ${
              isOuvindo
                ? 'text-blue-400'
                : localDetectado
                  ? 'text-emerald-400'
                  : 'text-slate-500'
            }`}>
              {isOuvindo
                ? 'OUVINDO PERITO...'
                : localDetectado
                  ? `AGUARDANDO CLIQUE: ${localDetectado}`
                  : suporteVoz
                    ? 'Aguardando comando...'
                    : 'Digite o cômodo abaixo'}
            </p>
          </div>
        </div>

        {/* TOGGLE MODO (só se voz for suportada) */}
        {suporteVoz && (
          <div className="flex items-center gap-1 bg-slate-900 rounded-2xl p-1 border border-white/5">
            {[
              { id: 'VOZ', label: 'Voz', icon: Mic },
              { id: 'MANUAL', label: 'Manual', icon: Keyboard },
            ].map(opt => {
              const Icone = opt.icon;
              const ativo = modo === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    if (isOuvindo) pararEscuta();
                    setModo(opt.id);
                    setErro(null);
                  }}
                  className={`px-4 py-2 rounded-xl text-[10px] tracking-widest flex items-center gap-2 transition-all ${
                    ativo
                      ? 'bg-amber-400 text-slate-950'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Icone size={12} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* GERENCIAR CÔMODOS */}
        <button
          type="button"
          onClick={() => setMostrarGerenciadorComodos(true)}
          className="px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 text-[10px] tracking-widest flex items-center gap-2 transition-all"
        >
          <Plus size={12} />
          CÔMODOS ({comodos.length})
        </button>
      </div>

      {/* =========================
          INPUT MANUAL (quando modo manual)
      ========================= */}
      {modo === 'MANUAL' && (
        <div className="bg-slate-950 px-6 lg:px-8 py-4 border-b border-white/5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <input
              type="text"
              value={inputManual}
              onChange={(e) => setInputManual(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') aplicarComodoManual(); }}
              placeholder="Digite o nome do cômodo (ex: COZINHA)"
              className="w-full h-11 bg-slate-900 border border-white/10 rounded-xl px-4 text-white text-[12px] outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 normal-case"
              list="comodos-lista"
            />
            <datalist id="comodos-lista">
              {comodos.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <button
            type="button"
            onClick={() => aplicarComodoManual()}
            disabled={!inputManual.trim()}
            className="bg-amber-400 text-slate-950 px-5 py-3 rounded-xl text-[10px] tracking-widest flex items-center gap-2 hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={14} />
            USAR
          </button>
        </div>
      )}

      {/* =========================
          ALERTAS
      ========================= */}
      {erro && (
        <div className="bg-red-950/50 border-y border-red-500/20 px-6 lg:px-8 py-4 flex items-start gap-4">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <span className="text-red-200 text-[11px] tracking-wider leading-relaxed normal-case flex-1">
            {erro}
          </span>
          <button
            type="button"
            onClick={() => setErro(null)}
            className="text-red-400 hover:text-red-300 transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* =========================
          ÁREA DA PLANTA
      ========================= */}
      <div className="relative bg-slate-950">
        {plantaImg ? (
          <>
            {/* INSTRUÇÃO FLUTUANTE */}
            <div className={`absolute top-4 left-4 z-20 px-5 py-2.5 rounded-full border text-[9px] tracking-widest backdrop-blur-md ${
              localDetectado
                ? 'bg-amber-400/95 border-amber-400 text-slate-950 animate-pulse'
                : 'bg-slate-900/90 border-white/10 text-slate-400'
            }`}>
              {localDetectado
                ? `CLIQUE PARA FIXAR: ${localDetectado}`
                : 'DEFINA UM CÔMODO ANTES DE CLICAR'}
            </div>

            {/* CONTADOR DE PINS */}
            {pins.length > 0 && (
              <button
                type="button"
                onClick={limparTodosPins}
                className="absolute top-4 right-4 z-20 bg-slate-900/90 backdrop-blur-md border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/30 px-4 py-2.5 rounded-full text-[9px] tracking-widest flex items-center gap-2 transition-all"
              >
                <Trash2 size={12} />
                LIMPAR {pins.length} PIN(S)
              </button>
            )}

            {/* IMAGEM + PINS */}
            <div
              ref={containerRef}
              onClick={confirmarPosicaoPin}
              className={`relative select-none ${
                localDetectado ? 'cursor-crosshair' : 'cursor-not-allowed'
              }`}
            >
              <img
                src={plantaImg}
                alt="Planta de vistoria"
                className="w-full h-auto object-contain block"
                draggable={false}
              />

              {/* PINS */}
              {pins.map((pin, idx) => (
                <div
                  key={pin.id}
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                  className="absolute -translate-x-1/2 -translate-y-full group/pin pointer-events-none"
                >
                  <div className="relative flex flex-col items-center">
                    {/* LABEL HOVER */}
                    <div className="mb-2 bg-slate-900 text-white text-[9px] tracking-widest px-3 py-1.5 rounded-lg whitespace-nowrap border border-amber-400/30 opacity-0 group-hover/pin:opacity-100 transition-opacity shadow-2xl">
                      {pin.local}
                    </div>

                    {/* PIN */}
                    <div className="bg-amber-500 text-slate-950 w-8 h-8 rounded-full shadow-[0_0_20px_rgba(245,158,11,0.6)] border-2 border-slate-950 flex items-center justify-center font-black text-[11px]">
                      {idx + 1}
                    </div>

                    {/* BOTÃO REMOVER */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removerPin(pin.id);
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white opacity-0 group-hover/pin:opacity-100 transition-opacity flex items-center justify-center pointer-events-auto"
                      title="Remover pin"
                    >
                      <X size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="py-32 text-center space-y-4">
            <Navigation size={64} className="mx-auto text-slate-800" />
            <p className="text-slate-600 text-[12px] tracking-[0.3em]">
              NENHUMA PLANTA CARREGADA
            </p>
            <p className="text-slate-700 text-[10px] tracking-widest normal-case max-w-md mx-auto">
              Faça o upload da planta ou croqui do imóvel para mapear as evidências
              por cômodo.
            </p>
          </div>
        )}
      </div>

      {/* =========================
          ESTATÍSTICAS POR CÔMODO
      ========================= */}
      {pins.length > 0 && (
        <div className="bg-slate-950 border-t border-white/5 px-6 lg:px-8 py-5">
          <span className="text-[9px] text-slate-500 tracking-[0.3em] block mb-3">
            DISTRIBUIÇÃO POR CÔMODO
          </span>
          <div className="flex flex-wrap gap-2">
            {Object.entries(estatisticas.porComodo).map(([comodo, qtd]) => (
              <span
                key={comodo}
                className="bg-white/5 border border-white/10 text-slate-300 text-[10px] px-3 py-1.5 rounded-full tracking-widest flex items-center gap-2"
              >
                <Circle size={6} className="fill-amber-400 text-amber-400" />
                {comodo}
                <span className="text-slate-500">{qtd}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* =========================
          RODAPÉ TÉCNICO
      ========================= */}
      <footer className="p-6 lg:p-8 bg-slate-900 border-t border-white/5 flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <Volume2 size={16} className="text-blue-400" />
          <span className="text-[9px] text-slate-400 tracking-[0.3em]">
            {suporteVoz
              ? 'IA DE PROCESSAMENTO FONÉTICO ATIVA'
              : 'MODO MANUAL · VOZ NÃO SUPORTADA NESTE NAVEGADOR'}
          </span>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className="text-[9px] text-slate-500 tracking-[0.3em] block">
              EVIDÊNCIAS MAPEADAS
            </span>
            <span className="text-amber-400 text-[18px] tracking-tight leading-none">
              {estatisticas.total}
            </span>
          </div>
          <ShieldCheck size={22} className="text-amber-400/50" />
        </div>
      </footer>

      {/* =========================
          GERENCIADOR DE CÔMODOS (MODAL)
      ========================= */}
      {mostrarGerenciadorComodos && (
        <div className="fixed inset-0 z-[500] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-6">
          <div className="bg-slate-900 w-full max-w-lg rounded-[3rem] border border-white/10 shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">

            {/* HEADER */}
            <div className="bg-slate-950 p-6 flex justify-between items-center border-b border-white/5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-amber-400 rounded-2xl flex items-center justify-center">
                  <MapPin size={20} className="text-slate-950" />
                </div>
                <div>
                  <h3 className="text-white text-[16px] tracking-tight leading-none">
                    Gerenciar Cômodos
                  </h3>
                  <p className="text-amber-400/70 text-[9px] tracking-[0.3em] mt-1">
                    {comodos.length} CADASTRADO(S)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMostrarGerenciadorComodos(false)}
                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
              >
                <X size={16} />
              </button>
            </div>

            {/* ADICIONAR */}
            <div className="p-6 border-b border-white/5 bg-slate-950/50 flex gap-2">
              <input
                type="text"
                value={novoComodo}
                onChange={(e) => setNovoComodo(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') adicionarComodo(); }}
                placeholder="Novo cômodo (ex: ESCRITÓRIO)"
                className="flex-1 h-11 bg-slate-900 border border-white/10 rounded-xl px-4 text-white text-[12px] outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 normal-case"
                maxLength={40}
              />
              <button
                type="button"
                onClick={adicionarComodo}
                disabled={!novoComodo.trim()}
                className="bg-amber-400 text-slate-950 px-5 py-3 rounded-xl text-[10px] tracking-widest flex items-center gap-2 hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Plus size={14} />
                ADICIONAR
              </button>
            </div>

            {/* LISTA */}
            <div className="p-6 overflow-y-auto flex-1 space-y-2">
              {comodos.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-slate-500 text-[10px] tracking-widest">
                    NENHUM CÔMODO CADASTRADO
                  </p>
                </div>
              ) : (
                comodos.map(c => {
                  const qtdPins = pins.filter(p => p.local === c).length;
                  return (
                    <div
                      key={c}
                      className="flex items-center justify-between bg-slate-950 border border-white/5 rounded-2xl px-4 py-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <MapPin size={14} className="text-amber-400 shrink-0" />
                        <span className="text-white text-[12px] tracking-wide truncate">
                          {c}
                        </span>
                        {qtdPins > 0 && (
                          <span className="bg-amber-400/10 text-amber-400 text-[9px] px-2 py-0.5 rounded-full tracking-widest shrink-0">
                            {qtdPins} pin(s)
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removerComodo(c)}
                        disabled={qtdPins > 0}
                        className="w-8 h-8 rounded-lg bg-white/5 text-slate-400 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:text-slate-400 shrink-0"
                        title={qtdPins > 0 ? 'Remova os pins primeiro' : 'Remover cômodo'}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* FOOTER */}
            <div className="p-6 border-t border-white/5 bg-slate-950/50 flex justify-between items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Restaurar lista padrão? Cômodos customizados serão perdidos.')) {
                    setComodos([...COMODOS_PADRAO]);
                  }
                }}
                className="text-slate-500 hover:text-amber-400 text-[10px] tracking-widest transition-colors flex items-center gap-2"
              >
                <Info size={12} />
                RESTAURAR PADRÃO
              </button>
              <button
                type="button"
                onClick={() => setMostrarGerenciadorComodos(false)}
                className="bg-white text-slate-950 px-6 py-3 rounded-xl text-[11px] tracking-widest hover:bg-amber-400 transition-all"
              >
                CONCLUÍDO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}